import { imagePath } from "../../../../utils/imagePath"

const AugmentedRealityData =  {
    slug: 'augmented-reality',
    sectionTitle: 'Introduction',
    blocks: [
      {
        type: 'text',
        content: [
          'Augmented Reality (AR) is a technology that overlays digital 3D objects, animations, sounds, and information onto the real-world environment in real time using a phone, tablet, or AR glasses.',
        ],
      },
    ],

    subSections: [
      {
        title: 'AR Tracking Types:',
        blocks: [
          {
            type: 'text',
            content:
              'With Spatial Grid, you can design a wide range of spatial experiences, including:',
          },
          {
            type: 'list',
            style: 'none',
            items: [
              '1.1 Image Target Tracking',
              '1.2 Surface Target Tracking',
              '1.3 World Tracking',
              '1.4 Location-Based AR (VPS)',
              '1.5 Area Tracking',
              '1.6 Face Tracking',
              '1.7 Body Tracking',
            ],
          },
        ],
      },

      {
        title: '1.1 Image Target Tracking:',
        blocks: [
          {
            type: 'text',
            content:
              'Image Target Tracking uses a printed or digital image as a reference point. When the AR system detects the image through the camera, it overlays virtual content on top of it.',
          },

          /* ---- How it works ---- */
          {
            type: 'text',
            content: '**How Image Target Tracking works:**',
          },
          {
            type: 'text',
            content:
              'When the camera recognizes this image, AR content—such as 3D models, animations, or videos—appears anchored to it.',
          },
          {
            type: 'text',
            content: '**Used for:**',
          },
          {
            type: 'list',
            items: [
              'Recognizing a predefined image',
              'Tracking its position, rotation, and size',
              'Triggering and attaching AR content',
              'Creating stable AR experiences in marketing, education, and products',
            ],
          },

          /* ---- Platform usage ---- */
          {
            type: 'text',
            content: '**How Image Target Tracking is used in this platform:**',
          },
          {
            type: 'text',
            content: [
              'i. Create a New Project: Start by creating a new project and selecting the Augmented Reality (AR) template.',
              'ii. Choose Target Type: A list of 7 AR target types will appear. Select Image Target to build an experience triggered by a real-world image.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.ChooseImageTarget,
            alt: 'Dashboard',
          },
          {
            type: 'text',
            content: ['iii. Enter Project Name:'],
          },
          {
            type: 'text',
            content: [' Provide a name for your project.'],
          },
          {
            type: 'image',
            src: imagePath?.ImageTargetProjectName,
            alt: 'Dashboard',
          },
          {
            type: 'text',
            content: ['iv. Select Marker Type:'],
          },
          {
            type: 'list',
            items: [
              'Choose the appropriate marker type based on how your AR system will detect the image.',
              'Single Marker – Uses one image as the tracking reference.',
              'Multi Marker – Uses multiple images combined as one target.',
            ],
          },
          {
            type: 'text',
            content: ['v. Select Target Shape:'],
          },
          {
            type: 'list',
            items: [
              'Flat – for posters, photos, magazines, packaging',
              'Cylinder – for bottles, cans, rolled labels',
              'Cone – for cone-shaped surfaces',
            ],
          },
        ],
      },

      {
        title: '1.2 Surface Target Tracking:',
        blocks: [
          {
            type: 'text',
            content:
              'Surface Tracking detects horizontal and vertical surfaces by analyzing camera input and device sensors. This allows users to place and interact with 3D objects in their real environment.',
          },
          {
            type: 'text',
            content: '**How Surface Target Tracking works:**',
          },
          {
            type: 'text',
            content:
              'A Surface Target allows AR to detect real-world flat surfaces such as floors, tables, or walls and place digital objects on them without needing a marker or image.',
          },
          {
            type: 'text',
            content:
              '**How Surface Target Tracking is used in this platform:**',
          },
          {
            type: 'text',
            content: ['**i. Create a New Project:**'],
          },
          {
            type: 'text',
            content: [
              ' Begin by creating a new project and selecting the **Augmented Reality (AR)** template.',
            ],
          },
          {
            type: 'text',
            content: ['**ii. Choose Target Type:**'],
          },
          {
            type: 'text',
            content: [
              '  A list of **seven AR target types** will appear. Select **Surface Target** to enable markerless AR that detects real-world flat surfaces.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.ChooseSurfaceTarget,
            alt: 'Dashboard',
          },
          {
            type: 'text',
            content: ['**iii. Enter Project Title:**'],
          },
          {
            type: 'text',
            content: [' Provide a suitable **title** for your AR experience.'],
          },
          {
            type: 'image',
            src: imagePath?.SurfaceTargetProjectName,
            alt: 'Dashboard',
          },
          {
            type: 'text',
            content: ['**iv. Add Comments:**'],
          },
          {
            type: 'text',
            content: [
              ' Include any additional notes or comments if needed, for example, project purpose, environment details, or testing notes.',
            ],
          },
        ],
      },
      {
        title: '1.3 World Tracking:',
        blocks: [
          {
            type: 'text',
            content: [
              'World Tracking uses advanced SLAM (Simultaneous Localization and Mapping) algorithms to understand and map the physical environment in real time. This allows for accurate placement and persistence of virtual objects within a 3D space as the user moves around.',
              'How world Tracking works:',
              'World Tracking allows AR applications to understand the device’s position and orientation in 3D space without using any markers.It enables digital objects to stay fixed in the real world as the user moves around.',
            ],
          },
          {
            type: 'text',
            content: '**Why World Tracking Is Used**',
          },
          {
            type: 'list',
            items: [
              'To allow **marker less AR experiences**',
              'To place objects anywhere in the environment',
              'For AR navigation, games, spatial apps, interior design, and product visualization',
              'To maintain **stable, persistent AR objects** in 3D space',
            ],
          },
          {
            type: 'text',
            content: '**How World Tracking is used in this platform:**',
          },
          {
            type: 'text',
            content: '**i. Create a New Project**',
          },
          {
            type: 'text',
            content: [
              'Start by creating a new project and selecting the Augmented Reality (AR) template.',
            ],
          },
          {
            type: 'text',
            content: ['**ii. Choose Target Type:**'],
          },
          {
            type: 'text',
            content: [
              ' When the list of seven AR target types appears, select World Tracking.This mode enables marker less AR where digital objects are placed and tracked in real-world 3D space.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.ChooseWorldTracking,
            alt: 'Dashboard',
          },
          {
            type: 'text',
            content: ['**iii. Enter Project Title**'],
          },
          {
            type: 'text',
            content: [
              'Provide a clear and descriptive title for your AR experience.',
            ],
          },
          {
            type: 'image',
            src: imagePath?.WorldTrackingProjectName,
            alt: 'Dashboard',
          },
          {
            type: 'text',
            content: ['**iv. Add Comments**'],
          },
          {
            type: 'text',
            content: [
              ' If necessary, include any comments or notes such as purpose, testing notes, or environmental requirements.',
            ],
          },
        ],
      },
      {
        title: '1.4 Location-Based AR (VPS):',
        blocks: [
          {
            type: 'text',
            content: [
              'Location-based AR places virtual content at real-world geographical coordinates using GPS, compass, Wi-Fi, and advanced Visual Positioning Systems (VPS). VPS improves precision by using visual cues from the environment.',
              'How location-based AR(VPS) works:',
              'Location-Based AR using VPS (Visual Positioning System) allows AR experiences to appear at precise real-world locations by combining GPS, camera data, and visual mapping of the environment.',
            ],
          },
          {
            type: 'text',
            content: '**How Location-Based AR(VPS) is used in this platform:**',
          },
          {
            type: 'text',
            content: '**i. Create a New Project**',
          },
          {
            type: 'text',
            content:
              'Start by creating a new project and selecting the Augmented Reality (AR) template.',
          },
          {
            type: 'text',
            content: ['**ii. Choose Target Type**'],
          },
          {
            type: 'text',
            content: [
              ' When the list of seven AR target types appears, select Location-Based AR (VPS) Tracking.This mode enables AR content to appear at specific real-world locations using VPS.',
            ],
          },
          { type: 'image', src: imagePath?.ChooseVps, alt: 'Dashboard' },
          {
            type: 'text',
            content: ['**iii. Enter Project Title**'],
          },
          {
            type: 'text',
            content: [' Provide a clear, descriptive title for your project.'],
          },
          { type: 'image', src: imagePath?.VpsProjectName, alt: 'Dashboard' },
          {
            type: 'text',
            content: ['**iv. Add Comments (Optional)**'],
          },
          {
            type: 'text',
            content: [' Include any comments or notes such as:'],
          },
          {
            type: 'list',
            items: [
              ' Location details',
              ' Coordinates',
              ' Purpose of the AR experience',
              'Testing instructions or environment notes',
            ],
          },
          {
            type: 'text',
            content: ['**v. Upload ZIP File (Required)**'],
          },
          {
            type: 'text',
            content: [
              ' Upload a ZIP file that contains the necessary VPS assets. The ZIP must include:',
            ],
          },
          {
            type: 'list',
            items: [
              ' A .glb file (3D model for placement)',
              ' A .json file (location and mapping data)',
              'ZIP file size must be 100 MB or less',
              'Ensure both .glb and .json are correctly packaged inside the ZIP',
            ],
          },
        ],
      },
      {
        title: '1.5 Area Tracking:',
        blocks: [
          {
            type: 'text',
            content: [
              'Area Target Tracking allows the AR system to recognize and understand entire physical environments such as rooms, stores, or building interiors. Once scanned, the space becomes an anchor for persistent and large-scale AR content.',
            ],
          },
          {
            type: 'text',
            content: ['**How area tracking works:**'],
          },
          {
            type: 'text',
            content: [
              'Area Tracking allows AR applications to recognize and track a pre-scanned 3D environment, such as a room, store layout, museum section, office space, or any structured area.It enables AR content to appear anchored at fixed positions within that real-world environment.',
            ],
          },
          { type: 'image', src: imagePath?.AreaTracking, alt: 'Dashboard' },
        ],
      },
      {
        title: '1.6 Face Tracking:',
        blocks: [
          {
            type: 'text',
            content: [
              'Face Tracking identifies facial landmarks—such as eyes, nose, mouth, and jawline—to accurately place virtual elements on the user’s face.',
            ],
          },
          {
            type: 'text',
            content: ['**How face tracking works:**'],
          },
          {
            type: 'text',
            content: [
              'Face Tracking allows AR applications to detect a human face and accurately track its movements, expressions, and landmarks in real time. This enables effects like face filters, masks, makeup try-ons, and expression-based animations.',
            ],
          },
          {
            type: 'text',
            content: ['**Why Face Tracking Is Used:**'],
          },
          {
            type: 'list',
            items: [
              'Face filters, masks, and effects',
              'Virtual try-ons (makeup, glasses, jewelry)',
              'Expression-based animations or avatars',
              'Social media AR lenses',
              'Character rigging and motion capture',
              'AR-based education and fitness apps',
            ],
          },
          { type: 'image', src: imagePath?.FaceTracking, alt: 'Dashboard' },
        ],
      },
      {
        title: '1.7 Body Tracking:',
        blocks: [
          {
            type: 'text',
            content: [
              'Body Tracking detects full-body movements using joint recognition (hips, knees, shoulders, wrists, etc.). This enables interactive experiences that respond to human movement.',
            ],
          },
          {
            type: 'text',
            content: ['**How body tracking works:**'],
          },
          {
            type: 'text',
            content: [
              'Body Tracking allows AR applications to detect and track full-body movement using the device camera. It identifies the user’s body, joints, and motion in real time, enabling AR effects that follow the body’s actions.',
            ],
          },
          {
            type: 'text',
            content: ['**Why Body Tracking Is Used:**'],
          },
          {
            type: 'list',
            items: [
              'Virtual try-ons (clothes, shoes, accessories)',
              'Fitness and workout AR apps',
              'Dance filters and movement-based effects',
              ' Full-body avatars or characters',
              ' Motion capture for animation',
              'Gaming and interaction systems',
            ],
          },
          { type: 'image', src: imagePath?.BodyTracking, alt: 'Dashboard' },
        ],
      },
    ],
  }
  export default AugmentedRealityData